import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { naira } from "../../lib/format";
import { Plus, Trash2 } from "lucide-react";

const BURG = { background: "#8B1E1E" };
const blank = () => ({ product_id: "", quantity: 1, unit_price: 0 });

export default function RequestDialog({ open, onClose, onSaved, products, warehouses, scoped, edit }) {
  const [form, setForm] = useState({ warehouse_id: "", reason: "" });
  const [lines, setLines] = useState([blank()]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    if (edit) { setForm({ warehouse_id: edit.warehouse_id, reason: edit.reason || "" }); setLines(edit.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, unit_price: i.unit_price }))); }
    else { setForm({ warehouse_id: warehouses[0]?.id || "", reason: "" }); setLines([blank()]); }
  }, [open, edit, warehouses]);
  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const pick = (i, pid) => { const p = products.find((x) => x.id === pid); setLines((ls) => ls.map((l, idx) => idx === i ? { ...l, product_id: pid, unit_price: l.unit_price || p?.cost || 0 } : l)); };
  const total = lines.reduce((s, l) => s + Number(l.quantity || 0) * Number(l.unit_price || 0), 0);
  const save = async (submit) => {
    const items = lines.filter((l) => l.product_id && Number(l.quantity) > 0).map((l) => ({ ...l, name: products.find((p) => p.id === l.product_id)?.name || "", quantity: Number(l.quantity), unit_price: Number(l.unit_price) }));
    if (!items.length || !form.warehouse_id) { toast.error("Warehouse and at least one item required"); return; }
    setSaving(true);
    try {
      let pr;
      if (edit) { pr = await api.put(`/purchase-requests/${edit.id}`, { ...form, items }); if (submit) pr = await api.post(`/purchase-requests/${edit.id}/submit`); }
      else pr = await api.post("/purchase-requests", { ...form, items, submit });
      toast.success(`${pr.number} ${submit ? "submitted for approval" : "saved as draft"}`); onSaved(); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pr-dialog">
        <DialogHeader><DialogTitle className="font-brand">{edit ? `Edit ${edit.number}` : "New Purchase Request"}</DialogTitle><DialogDescription>Requests are location-scoped and go through the configured approval levels before a PO can be raised.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5"><Label>Receiving warehouse *</Label>
            <Select value={form.warehouse_id} onValueChange={(v) => setForm({ ...form, warehouse_id: v })} disabled={scoped}>
              <SelectTrigger data-testid="pr-warehouse"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>{warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label>Reason</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why is this needed?" data-testid="pr-reason" /></div>
        </div>
        <div className="mt-2">
          <div className="flex items-center justify-between mb-2"><Label>Items</Label><Button variant="outline" size="sm" onClick={() => setLines((l) => [...l, blank()])} data-testid="pr-add-line"><Plus size={14} className="mr-1" /> Add Line</Button></div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6"><Select value={l.product_id} onValueChange={(v) => pick(i, v)}><SelectTrigger data-testid={`pr-line-product-${i}`}><SelectValue placeholder="Item…" /></SelectTrigger>
                  <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
                <Input className="col-span-2" type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`pr-line-qty-${i}`} />
                <Input className="col-span-3" type="number" value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} data-testid={`pr-line-price-${i}`} />
                <button className="col-span-1 flex justify-center" style={{ color: "#A52222" }} onClick={() => setLines((ls) => ls.filter((_, idx) => idx !== i))} data-testid={`pr-line-remove-${i}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <div className="text-right mt-3 font-brand font-bold text-lg" style={{ color: "#8B1E1E" }} data-testid="pr-total">Estimated total: {naira(total)}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="outline" onClick={() => save(false)} disabled={saving} data-testid="pr-save-draft">Save Draft</Button>
          <Button onClick={() => save(true)} disabled={saving} data-testid="pr-submit" style={BURG} className="text-white">{saving ? "Saving…" : "Submit for Approval"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
